import React, { useState } from 'react';
import { Mail, ExternalLink, Moon, Sun, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Home = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Profile image - place your image file at: public/images/profile.jpg
  const profileImage = '/images/profile.jpg';

  const researchInterests = [
    'Wetland conservation',
    'Antibiotic resistance',
    'Mass spectrometry',
    'Genomics',
    'Environment surveillance'
  ];

  const education = [
    {
      degree: 'PhD in Life sciences',
      year: '2015-2021',
      institution: 'Centre for Cellular and Molecular Biology, Hyderabad'
    },
    {
      degree: 'B.Tech in Biotechnology',
      year: '2008-2012',
      institution: 'Amity University'
    }
  ];

  const featuredPublications = [
    {
      title: 'Dissecting Daboia: Investigating synergistic effects of Russell\'s viper venom toxins',
      authors: 'Gotravalli V. Rudresha, Siddharth Bhatia et al.',
      journal: 'International Journal of Biological Macromolecules',
      year: '2025',
      doi: 'https://doi.org/10.1016/j.ijbiomac.2025.146533',
      abstract: 'This study reveals that Russell\'s viper venom becomes much more dangerous when different toxins work together. By separating the venom into its components, we found that combinations of certain toxins cause far more damage than individual toxins alone. Importantly, blocking just one type of toxin can effectively neutralize the venom\'s harmful effects, offering new hope for better snakebite treatments.'
    }
  ];

  const recentPublications = [
    {
      title: 'From birth to bite: the evolutionary ecology of India\'s medically most important snake venoms',
      authors: 'R.R. Senji Laxme, S. Khochare, S. Bhatia, G. Martin, K. Sunagar',
      journal: 'BMC Biology',
      year: '2024',
      doi: 'https://link.springer.com/article/10.1186/s12915-024-01960-8'
    },
    {
      title: 'Elusive elapids: biogeographic venom variation in Indian kraits and its repercussion on snakebite therapy',
      authors: 'U. Rashmi, S. Bhatia, M. Nayak, S. Khochare, K. Sunagar',
      journal: 'Frontiers in Pharmacology',
      year: '2024',
      doi: 'https://doi.org/10.3389/fphar.2024.1443073'
    },
    {
      title: 'The Hidden Arsenal: Venomics and antivenomics of medically important yet neglected snakes from India',
      authors: 'A. Jaglan, S. Bhatia, G. Martin, K. Sunagar',
      journal: 'Toxicon',
      year: '2024',
      doi: 'https://doi.org/10.1016/j.toxicon.2024.107945'
    },
    {
      title: 'Multilevel Comparison of Indian Naja Venoms and Their Cross-Reactivity with Indian Polyvalent Antivenoms',
      authors: 'A. Deka, S. Bhatia, V. Santra, O.K. Bharti, H.T. Lalremsanga, G. Martin, K. Sunagar',
      journal: 'Toxins',
      year: '2023',
      doi: 'https://doi.org/10.3390/toxins15040258'
    }
  ];

  const socialLinks = [
    {
      name: 'Email',
      url: 'mailto:siddharth.bhatia17@gmail.com',
      icon: <img src="/images/email_logo.png" alt="Email" className="w-5 h-5 object-contain" />
    },
    {
      name: 'Google Scholar',
      url: 'https://scholar.google.co.in/citations?user=fOGLJKwAAAAJ&hl=en&oi=ao',
      icon: <img src="/images/googlescholar_logo.png" alt="Google Scholar" className="w-5 h-5 object-contain" />
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/siddharth-bhatia-b4aa4244/',
      icon: <img src="/images/linkedin_logo.png" alt="LinkedIn" className="w-5 h-5 object-contain" />
    },
    {
      name: 'Twitter',
      url: '#', // Update with your Twitter URL
      icon: <img src="/images/twitter_logo.png" alt="Twitter" className="w-5 h-5 object-contain" />
    },
    {
      name: 'Instagram',
      url: '#', // Update with your Instagram URL
      icon: <img src="/images/instagram_logo.png" alt="Instagram" className="w-5 h-5 object-contain" />
    },
    {
      name: 'Substack',
      url: 'https://substack.com/@evolvedrabbit',
      icon: <img src="/images/substack_logo.png" alt="Substack" className="w-5 h-5 object-contain" />
    }
  ];

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'dark' : ''}`}>
      {/* Top Navigation Bar */}
      <nav className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 sticky top-0 z-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Left: Name and Desktop Navigation */}
            <div className="flex items-center gap-8">
              <a 
                href="#home" 
                className="text-xl font-serif font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors absolute left-1/2 transform -translate-x-1/2 lg:relative lg:left-0 lg:transform-none"
              >
                Siddharth Bhatia
              </a>
              
              {/* Desktop Navigation Links */}
              <div className="hidden lg:flex items-center gap-6">
                <a href="#about" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                  About
                </a>
                <a href="#research" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                  Research
                </a>
                <a href="#publications" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                  Publications
                </a>
              </div>
            </div>

            {/* Right: Dark Mode Toggle */}
            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 py-4">
              <div className="flex flex-col gap-4">
                <a 
                  href="#about" 
                  onClick={toggleMobileMenu}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors px-4 py-2"
                >
                  About
                </a>
                <a 
                  href="#research" 
                  onClick={toggleMobileMenu}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors px-4 py-2"
                >
                  Research
                </a>
                <a 
                  href="#publications" 
                  onClick={toggleMobileMenu}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors px-4 py-2"
                >
                  Publications
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="bg-white dark:bg-gray-900 transition-colors">
        {/* Hero and About Section - Side by Side */}
        <section id="home" className="border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-0">
              {/* Left Side - Hero Section */}
              <div className="lg:col-span-2 border-r border-gray-200 dark:border-gray-700 px-6 lg:px-12 py-16">
                <div className="flex flex-col items-center text-center lg:sticky lg:top-24">
                  <img
                    src={profileImage}
                    alt="Siddharth Bhatia"
                    className="w-48 h-48 rounded-full object-cover mb-6 shadow-lg"
                  />
                  <h1 className="text-4xl lg:text-5xl font-serif font-bold text-gray-900 dark:text-white mb-3">
                    Siddharth Bhatia
                  </h1>
                  <h2 className="text-xl lg:text-2xl text-gray-600 dark:text-gray-400 mb-2">
                    Project Scientist-II
                  </h2>
                  <p className="text-lg lg:text-xl text-gray-500 dark:text-gray-400 mb-6">
                    Tata Institute for Genetics and Society
                  </p>
                  <div className="flex flex-row gap-4 items-center justify-center">
                    {socialLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-all hover:scale-110"
                        title={link.name}
                      >
                        {link.icon}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Side - About Section */}
              <div id="about" className="lg:col-span-3 px-6 lg:px-12 py-16">
                <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-8">About me</h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                    I am a researcher at the Tata Institute for Genetics and Society (TIGS) in Bengaluru, India. My current work focuses on tracking antibiotic resistant pathogens in the environment and understanding how they spread.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                    Earlier, I worked on snake venoms. I studied how venom composition changes across habitats and what that means for treatment. This work shaped my interest in the link between ecology, evolution, and human health.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                    I have always felt close to nature. I spend a lot of my free time visiting wetlands around Bengaluru, watching birds, and photographing wildlife. If you would like to see my bird photographs, you can find them <a href="https://www.inaturalist.org/people/siddharth-bhatia" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline">here</a>.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                    I also write occasionally about ideas, questions, and things I am trying to understand. You can read my notes and essays on Substack <a href="https://substack.com/@evolvedrabbit" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline">here</a>.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    I want my research to help people stay healthier while also improving how we understand the natural world.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Research Interests - Full Width */}
      <section id="research" className="border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-6">Research Interests</h2>
          <div className="flex flex-wrap gap-3">
            {researchInterests.map((interest, index) => (
              <Badge key={index} variant="secondary" className="px-4 py-2 text-base">
                {interest}
              </Badge>
            ))}
          </div>

          <div className="mt-16">
            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-8">Education</h2>
            <div className="space-y-6">
              {education.map((edu, index) => (
                <div key={index} className="border-l-4 border-blue-600 dark:border-blue-400 pl-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{edu.degree}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{edu.year}</p>
                  <p className="text-gray-700 dark:text-gray-300">{edu.institution}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Publications - Full Width */}
      <section id="publications" className="border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-8">Featured Publications</h2>
        <div className="space-y-8">
          {featuredPublications.map((pub, index) => (
            <Card key={index} className="p-6 hover:shadow-lg transition-shadow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                <a href={pub.doi} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  {pub.title}
                </a>
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-3">
                {pub.authors} ({pub.year}). <em>{pub.journal}</em>
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">{pub.abstract}</p>
              <a
                href={pub.doi}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium inline-flex items-center gap-1"
              >
                DOI <ExternalLink className="w-4 h-4" />
              </a>
            </Card>
          ))}
        </div>
        </div>
      </section>

      {/* Recent Publications - Full Width */}
      <section className="border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-8">Recent Publications</h2>
        <div className="space-y-4">
          {recentPublications.map((pub, index) => (
            <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                <a href={pub.doi} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  {pub.title}
                </a>
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {pub.authors && `${pub.authors}. `}{pub.journal}, {pub.year}
              </p>
              <a
                href={pub.doi}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm inline-flex items-center gap-1 mt-2"
              >
                DOI <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button variant="outline" asChild className="dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700">
            <a
              href="https://scholar.google.co.in/citations?user=fOGLJKwAAAAJ&hl=en&oi=ao"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2"
            >
              View all publications on Google Scholar <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 text-center text-gray-600 dark:text-gray-400">
          <p>© {new Date().getFullYear()} Siddharth Bhatia. All rights reserved.</p>
        </div>
      </footer>
      </main>
    </div>
  );
};

export default Home;